import React from 'react';
import { MissionIdParams } from '@src/core/entities';
import { useCancelMissionMutation, useGetOrderMissionQuery } from '@src/features/order/redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EnterRouteForm, SidebarLoading } from '@src/features/order/pages/upsert-mission-page/components';
import { IRouteForm } from '@src/features/order/pages/upsert-mission-page/entities';
import { useForm } from 'react-hook-form';
import { OrderMissionEntity } from '@src/core/features';
import { EMPTY_VALUE } from '@src/core/config';
import { ArcStack, ArcToolbar } from '@src/core/ui';
import { selectTerrainMode, selectWaypoints, useUpdateMissionMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { MissionWaypoint } from '@src/features/order/pages/upsert-mission-page/redux/types';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { routesConfig } from '@src/core/routes';
import { getUnitType } from '@src/core/features/settings/units';
import { UnitType, UnitSystemType } from '@src/core/enums';
import { arcConvertBetweenUnitTypes, arcToFixed } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  resolveMissionRouteBackNavigation,
  resolveMissionRouteForwardNavigation,
} from '@src/features/order/navigation';

// REST mission values are SI (m, m/s); form inputs are labeled in the user's display unit.
const siToDisplay = (value: number | string | undefined | null, measurement: MeasurementType): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY_VALUE;
  const converted = arcConvertBetweenUnitTypes(num, measurement, UnitType.SI, getUnitType());
  return arcToFixed(converted, 1);
};

const getDeliveryMode = (data: OrderMissionEntity) => {
  const [ order ] = data.orders;
  return order.deliveryMode;
};

const getOrderWP = (data: OrderMissionEntity) => {
  const orderWP = data.waypoints.find((w) => Boolean(w.orderId));
  return orderWP;
};

const getTakeOff = (data: OrderMissionEntity) => {
  const [wp] = data.waypoints;
  return wp;
};

const UpdateRouteSidebar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const terrainMode = useSelector(selectTerrainMode);
  const params = useParams<MissionIdParams>();
  const { data, isLoading } = useGetOrderMissionQuery(params.missionId);
  
  const [updateMission, { isLoading: isSubmitting }] = useUpdateMissionMutation();
  const [ cancelMission, { isLoading: isCanceling } ] = useCancelMissionMutation();

  const isStepByStep = Boolean(searchParams.get('step-by-step'));
  const orderId =
    searchParams.get('orderId') ?? data?.orders?.[0]?.id;

  const goBack = () => {
    if (isStepByStep) {
      const { path, replace } = resolveMissionRouteBackNavigation(
        'update-mission',
        orderId,
        true,
      );
      navigate(path, { replace });
      return;
    }
    navigate(-1);
  };

  const onCancel = () => {
    cancelMission(params.missionId).unwrap().then(() => {
      toast.success('Mission has been canceled');
      navigate(routesConfig.orders, { replace: true });
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'cancelMissionUpdateRoute' } });
    });
  };

  const onSubmit = (form: IRouteForm, waypoints: MissionWaypoint[]) => {
    const missionId = params.missionId;
    if (!missionId) return;

    // Waypoints are already converted to SI in toMissionWaypoints; the wire is always
    // SI, so unitSystem is a constant guard (UNITS Standard R2/R6).
    updateMission({
      missionId,
      deliveryMode: form.deliveryMode,
      altitudeMode: terrainMode,
      unitSystem: UnitSystemType.METRIC,
      waypoints,
    }).unwrap().then(() => {
      toast.success('Mission has been updated');
      const { path, replace } = resolveMissionRouteForwardNavigation(
        missionId,
        {
          orderId,
          droneSerial: data?.drone?.serialNumber,
        },
        isStepByStep,
      );
      if (isStepByStep) {
        navigate(path, { replace });
        return;
      }
      navigate(-1);
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'updateMission' } });
    });
  };

  const form = useForm<IRouteForm>({ 
    defaultValues: {
      waypoints: [],
      speed: EMPTY_VALUE,
      deliveryMode: EMPTY_VALUE,
      takeOffAltitude: EMPTY_VALUE,
      deliveryAltitude: EMPTY_VALUE,
      winchAltitude: EMPTY_VALUE,
      destinationAltitude: EMPTY_VALUE,
    } 
  });


  React.useEffect(() => {
 
    if (!data) return;

    const orderWp = getOrderWP(data);
    const takeOffWp = getTakeOff(data);

    form.setValue('deliveryMode', getDeliveryMode(data));
    form.setValue('speed', siToDisplay(orderWp?.speed, MeasurementType.SPEED));
    form.setValue('deliveryAltitude', siToDisplay(orderWp?.deliveryAltitude, MeasurementType.DISTANCE));
    form.setValue('winchAltitude', siToDisplay(orderWp?.deliveryAltitude, MeasurementType.DISTANCE));
    form.setValue('takeOffAltitude', siToDisplay(takeOffWp?.altitude, MeasurementType.DISTANCE));
    form.setValue('destinationAltitude', siToDisplay(orderWp?.altitude, MeasurementType.DISTANCE));
  }, [data]);


  if (isLoading) {
    return <SidebarLoading />;
  }

  return (
    <ArcStack spacing={2} height='100%'>
      <ArcToolbar 
        title='3. Enter route parameters' 
        goBack={goBack} />
      <EnterRouteForm 
        form={form} 
        goBack={onCancel} 
        onSubmit={onSubmit} 
        isCanceling={isCanceling}
        isSubmitting={isSubmitting} />
    </ArcStack>
  );
};

export default UpdateRouteSidebar;